import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { ArrowUp, ArrowDown, Sparkles } from "lucide-react";
import { FreelancerSuggestionCard } from "./FreelancerSuggestionCard";
import type { FreelancerSugerido } from "./useVagaAssistant";

interface DualFreelancerCardsListProps {
  /** Cards da PÁGINA atual da lista primária. */
  primary: FreelancerSugerido[];
  /** Cards da PÁGINA atual da lista "outras opções". */
  exploratory: FreelancerSugerido[];
  /** Ordem completa de IDs (todas as páginas) pra calcular posição/delta. */
  idsPrimary: string[];
  idsExploratory: string[];
  /** Quantos perfis exploratory ainda não têm avaliação (resumo do topo). */
  novosLearnedCount?: number;
  onSelecionados?: (freelancerId: string) => void;
  onShortlist?: (freelancerId: string) => void;
  onDescartar?: (freelancer: FreelancerSugerido) => void;
  isProcessing?: boolean;
}

/** Dimensões que a lista aprendida valoriza (linguagem do negócio, sem jargão
 * interno). Usadas pra explicar por que um freelancer sobe nessa lista. */
const DIMENSOES_APRENDIDAS: { key: string; label: string }[] = [
  { key: "value_match_score", label: "valor" },
  { key: "score_composto_score", label: "qualidade" },
  { key: "semantic_score", label: "aderência ao briefing" },
];

/** Dimensão valorizada pela lista aprendida em que o freelancer é forte. */
function destaqueAprendido(f: FreelancerSugerido): string | null {
  const bd = f.match_breakdown;
  if (!bd) return null;
  let best: { label: string; v: number } | null = null;
  for (const d of DIMENSOES_APRENDIDAS) {
    const v = bd[d.key];
    if (typeof v === "number" && (!best || v > best.v)) best = { label: d.label, v };
  }
  return best && best.v >= 60 ? best.label : null;
}

/** Cabeçalho + meta (delta de posição, exclusivo, destaque) de cada card. */
function CardComMeta({
  freelancer,
  posicao,
  posicaoOutra,
  isLearned,
  onSelecionados,
  onShortlist,
  onDescartar,
  isProcessing,
}: {
  freelancer: FreelancerSugerido;
  posicao: number;
  posicaoOutra: number | undefined;
  isLearned: boolean;
  onSelecionados?: (id: string) => void;
  onShortlist?: (id: string) => void;
  onDescartar?: (freelancer: FreelancerSugerido) => void;
  isProcessing?: boolean;
}) {
  const exclusivo = posicaoOutra === undefined;
  // delta > 0: está mais acima nesta lista do que na outra.
  const delta = posicaoOutra === undefined ? 0 : posicaoOutra - posicao;
  const destaque = isLearned ? destaqueAprendido(freelancer) : null;

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-1.5 px-0.5">
        <span className="text-[11px] font-medium text-muted-foreground">#{posicao + 1}</span>
        {exclusivo && (
          <Badge variant="secondary" className="h-4 px-1.5 text-[10px] font-medium">
            só nesta lista
          </Badge>
        )}
        {!exclusivo && delta > 0 && (
          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-600">
            <ArrowUp className="h-3 w-3" />
            {delta}
          </span>
        )}
        {!exclusivo && delta < 0 && (
          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-rose-500">
            <ArrowDown className="h-3 w-3" />
            {Math.abs(delta)}
          </span>
        )}
        {destaque && (
          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-600">
            <Sparkles className="h-3 w-3" />
            forte em {destaque}
          </span>
        )}
      </div>
      <FreelancerSuggestionCard
        freelancer={freelancer}
        onSelecionados={onSelecionados}
        onShortlist={onShortlist}
        onDescartar={onDescartar}
        isProcessing={isProcessing}
      />
    </div>
  );
}

/**
 * Renderiza as duas listas do busca_match (dual) lado a lado dentro do chat:
 * "Recomendados" (calibrada) e "Outras opções" (aprendida por regressão sobre o
 * histórico de seleção). Deixa claras as diferenças entre elas: resumo no topo,
 * badge de exclusivo, delta de posição e o que puxa cada perfil na lista
 * aprendida.
 *
 * Recebe só a PÁGINA atual de cada lista; a ordem completa de IDs (idsPrimary/
 * idsExploratory) vem dos metadados pra calcular posição e delta sobre o
 * conjunto inteiro, sem precisar carregar todos os cards.
 */
export function DualFreelancerCardsList({
  primary,
  exploratory,
  idsPrimary,
  idsExploratory,
  novosLearnedCount = 0,
  onSelecionados,
  onShortlist,
  onDescartar,
  isProcessing,
}: DualFreelancerCardsListProps) {
  const { rankPrimary, rankLearned, comuns, soPrimary, soLearned } = useMemo(() => {
    const rankPrimary = new Map<string, number>();
    idsPrimary.forEach((id, i) => rankPrimary.set(id, i));
    const rankLearned = new Map<string, number>();
    idsExploratory.forEach((id, i) => rankLearned.set(id, i));

    const idsP = new Set(idsPrimary);
    const idsL = new Set(idsExploratory);
    const comuns = [...idsP].filter((id) => idsL.has(id)).length;
    return {
      rankPrimary,
      rankLearned,
      comuns,
      soPrimary: idsPrimary.length - comuns,
      soLearned: idsExploratory.length - comuns,
    };
  }, [idsPrimary, idsExploratory]);

  return (
    <div className="space-y-2">
      {/* Resumo no topo: o que muda entre as duas listas. */}
      <div className="rounded-md border border-border/50 bg-muted/40 px-2.5 py-1.5 text-[11px] text-muted-foreground">
        <span className="font-medium text-foreground/70">{comuns}</span> em comum
        {" · "}
        <span className="font-medium text-foreground/70">{soPrimary}</span> só em Recomendados
        {" · "}
        <span className="font-medium text-foreground/70">{soLearned}</span> só em Outras opções
        {novosLearnedCount > 0 && (
          <>
            {" · "}
            <span className="font-medium text-amber-600">{novosLearnedCount}</span> sem avaliação ainda
            {" (chance pra perfis novos)"}
          </>
        )}
      </div>

      <div className="grid grid-cols-1 gap-x-4 gap-y-3 lg:grid-cols-2">
        <section className="space-y-2 min-w-0">
          <div className="px-0.5">
            <div className="text-xs font-semibold text-foreground">Recomendados</div>
            <div className="text-[11px] text-muted-foreground">
              Lista principal, calibrada. Traz um leque diverso, incluindo perfis novos com potencial.
            </div>
          </div>
          {primary.map((f) => (
            <CardComMeta
              key={f.id}
              freelancer={f}
              posicao={rankPrimary.get(f.id) ?? 0}
              posicaoOutra={rankLearned.get(f.id)}
              isLearned={false}
              onSelecionados={onSelecionados}
              onShortlist={onShortlist}
              onDescartar={onDescartar}
              isProcessing={isProcessing}
            />
          ))}
        </section>

        <section className="space-y-2 min-w-0">
          <div className="px-0.5">
            <div className="text-xs font-semibold text-foreground">Outras opções</div>
            <div className="text-[11px] text-muted-foreground">
              Lista aprendida com o histórico de seleção. Destaca o perfil que o time costuma escolher.
            </div>
          </div>
          {exploratory.map((f) => (
            <CardComMeta
              key={f.id}
              freelancer={f}
              posicao={rankLearned.get(f.id) ?? 0}
              posicaoOutra={rankPrimary.get(f.id)}
              isLearned
              onSelecionados={onSelecionados}
              onShortlist={onShortlist}
              onDescartar={onDescartar}
              isProcessing={isProcessing}
            />
          ))}
        </section>
      </div>
    </div>
  );
}
